import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { UserMenu } from './UserMenu';
import NotificationBell from './NotificationBell';
import {
  LogOut,
  User,
  CreditCard,
  Link as LinkIcon,
  Copy,
  X,
  Menu,
  LayoutGrid,
  Building2,
  Palette,
} from 'lucide-react';
import AdminMenuList from './AdminMenuList';
import { ACCESS_LEVELS } from '@rti/shared';
import { useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';

const PLAN_BADGES: Record<number, { label: string; text: string; bg: string; border: string }> = {
  [ACCESS_LEVELS.BASE]: { label: 'Base', text: 'text-rs-light-purple', bg: 'bg-rs-light-purple/10', border: 'border-rs-light-purple/30' },
  [ACCESS_LEVELS.COMPOUND]: { label: 'Compound', text: 'text-rs-light-green', bg: 'bg-rs-light-green/10', border: 'border-rs-light-green/30' },
  [ACCESS_LEVELS.PRIME]: { label: 'Prime', text: 'text-rs-salmon', bg: 'bg-rs-salmon/10', border: 'border-rs-salmon/30' },
  [ACCESS_LEVELS.EMPLOYEE]: { label: 'Employee', text: 'text-rs-salmon', bg: 'bg-rs-salmon/10', border: 'border-rs-salmon/30' },
};

const TopNavbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const isEmployee = user?.effective_membership_id === ACCESS_LEVELS.EMPLOYEE;
  const { getFlag, isLoading: isFeatureFlagsLoading } = useFeatureFlagsContext();
  const { isDark, toggle: toggleTheme } = useTheme();
  // Light-mode chrome: frosted green glass with borderless soft-shadow elevation.
  const chromeStyle: React.CSSProperties | undefined = isDark
    ? undefined
    : {
        background: 'rgba(231,242,224,0.6)',
        borderColor: 'transparent',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        boxShadow: '0 1px 0 rgba(16,24,16,0.06), 0 10px 28px -14px rgba(16,24,16,0.30)',
      };
  const navigate = useNavigate();
  const showThemeToggle = !isFeatureFlagsLoading && getFlag('theme_toggle');
  const billingEnabled = !isFeatureFlagsLoading && getFlag('enable_billing');
  const showCompanySettings = !isFeatureFlagsLoading && getFlag('company_settings');
  const planBadge = isAuthenticated && user ? PLAN_BADGES[user.effective_membership_id ?? 0] : undefined;
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(1);
  const [duration, setDuration] = useState(60);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  const parentGroupName = (((user as any)?.parent_group_name) || '').trim();
  const subCompanyName = (((user as any)?.sub_company_name) || '').trim();
  const companyName = (user?.company_name || '').trim();
  const effectiveCompanyName = subCompanyName || companyName;
  const companyPrefix = parentGroupName ? `[${parentGroupName}] ${effectiveCompanyName}:` : effectiveCompanyName;
  const identityLabel = companyPrefix ? `${companyPrefix}: ${fullName}` : fullName;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated) return null;

  const handleSignOut = async () => {
    setShowSignOutConfirm(false);
    setIsMenuOpen(false);
    await logout();
    navigate('/authorization');
  };

  const handleToggleTheme = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    toggleTheme();
    setIsMenuOpen(false);
  };

  const b64EncodeNum = (n: number): string => {
    const encoded = btoa(n.toString());
    return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const handleGenerate = () => {
    const m = b64EncodeNum(selectedMembership);
    const t = b64EncodeNum(duration);
    const url = `${window.location.origin}/free-trial?m=${m}&t=${t}`;
    setGeneratedUrl(url);
  };

  const copyToClipboard = async (text: string) => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        return false;
      }
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        return true;
      } catch {
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  return (
    <div
      className="sticky top-0 z-40 bg-rs-off-black border-b border-rs-jet-light"
      style={chromeStyle}
    >
      <div className="flex items-center justify-between h-20 ml-3 px-6">
        <div className="flex items-center gap-4 py-5">
          <Link to="/dashboard" className="flex items-center gap-2">
              <img
                  className="h-[45px]"
                  src={isDark ? "/logos/PrimaryRTi/White.svg" : "/logos/PrimaryRTi/LimeBlack.svg"}
                  alt="Resinsmart Logo"
              />
          </Link>
          {planBadge && (
            <span className={`text-xs font-medium px-3 py-1 rounded-full border ${planBadge.bg} ${planBadge.text} ${planBadge.border}`}>
              {planBadge.label}
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          {!isFeatureFlagsLoading && getFlag('notification_center') && <NotificationBell />}
          {billingEnabled ? (
            <UserMenu />
          ) : (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg text-rs-off-white hover:bg-rs-jet-light transition-colors"
              >
                <span className="text-sm hidden md:inline max-w-[32rem] truncate">{identityLabel}</span>
                {isMenuOpen ? (
                  <X size={20} className="transition-transform" />
                ) : (
                  <Menu size={20} className="transition-transform" />
                )}
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-rs-jet border border-rs-jet-light rounded-lg shadow-lg py-2 z-50">
                  <div className="px-4 py-2 border-b border-rs-jet-light">
                    <p className="text-sm font-medium text-rs-off-white">{identityLabel}</p>
                    <p className="text-xs text-rs-off-white/60">{user?.email}</p>
                  </div>

                  <div className="py-2">
                    <Link
                      to="/account"
                      className="flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User size={18} className="text-rs-green" />
                      <span className="text-sm">Account Settings</span>
                    </Link>

                    {showCompanySettings && (
                      <Link
                        to="/company-settings"
                        className="flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Building2 size={18} className="text-rs-green" />
                        <span className="text-sm">Company Settings</span>
                      </Link>
                    )}

                    {billingEnabled && (
                      <Link
                        to="/billing"
                        className="flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <CreditCard size={18} className="text-rs-green" />
                        <span className="text-sm">Billing</span>
                      </Link>
                    )}

                  </div>

                  {(user?.is_admin || isEmployee) && (
                    <>
                      <div className="border-t border-rs-jet-light my-2"></div>
                      <div className="px-4 py-2">
                        <p className="text-xs font-semibold text-rs-green uppercase tracking-wide">Admin</p>
                      </div>
                      <div className="py-2">
                        {isEmployee && (
                          <Link
                            to="/system"
                            className="flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <LayoutGrid size={18} className="text-rs-green" />
                            <span className="text-sm">System Hub</span>
                          </Link>
                        )}
                        <AdminMenuList
                          onClose={() => setIsMenuOpen(false)}
                          onAction={(actionId) => {
                            if (actionId === 'generate_free_trial') {
                              setIsTrialModalOpen(true);
                            }
                          }}
                          iconSize={18}
                        />
                      </div>
                    </>
                  )}

                  <div className="border-t border-rs-jet-light mt-2 pt-2">
                    {showThemeToggle && (
                      <Link
                        to="#"
                        onClick={handleToggleTheme}
                        className="flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors w-full"
                      >
                        <Palette size={18} className="text-rs-green" />
                        <span className="text-sm">Appearance</span>
                      </Link>
                    )}
                    <Link
                        to='#'
                      onClick={() => {
                        setIsMenuOpen(false);
                        setShowSignOutConfirm(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors w-full border-t border-rs-jet-light mt-1"
                    >
                      <LogOut size={18} className="text-rs-red" />
                      <span className="text-sm">Sign Out</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showSignOutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.7)]">
          <div
            role="dialog"
            aria-modal="true"
            className="bg-rs-off-black rounded-lg shadow-lg max-w-sm w-full p-6 relative"
          >
            <h2 className="text-xl text-rs-dark-green mb-3">Leaving so soon?</h2>
            <p className="text-base text-rs-off-white mb-6">Are you sure you want to sign out of your account?</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSignOutConfirm(false)}
                className="secondary px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSignOut}
                className="green px-4 py-2 rounded"
              >
                Sign Out
              </button>
            </div>
            <button
              onClick={() => setShowSignOutConfirm(false)}
              aria-label="Close"
              className="absolute top-2 right-2 text-sm opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {isTrialModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-rs-off-black p-6 rounded-lg max-w-md w-full mx-4 border border-rs-jet-light">
            <h3 className="text-xl text-rs-off-white mb-4">Generate Free Trial Link</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-rs-off-white mb-1">Membership</label>
              <select
                value={selectedMembership}
                onChange={(e) => setSelectedMembership(Number(e.target.value))}
                className="w-full p-2 border border-rs-jet-light rounded bg-rs-jet text-rs-off-white"
              >
                {Object.entries(ACCESS_LEVELS).filter(([name]) => name !== 'EMPLOYEE').map(([name, id]) => (
                  <option key={id} value={id}>{name.charAt(0) + name.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-rs-off-white mb-1">Duration (days)</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full p-2 border border-rs-jet-light rounded bg-rs-jet text-rs-off-white"
                min="1"
              />
            </div>
            {generatedUrl && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-rs-off-white mb-1">Generated URL</label>
                <input
                  type="text"
                  value={generatedUrl}
                  readOnly
                  className="w-full p-2 border border-rs-jet-light rounded bg-rs-jet text-rs-green"
                />
                <button
                  onClick={async () => {
                    const success = await copyToClipboard(generatedUrl);
                    setCopied(success);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  className="mt-2 flex items-center gap-2 px-4 py-2 bg-rs-dark-green !text-rs-off-black rounded hover:bg-rs-green transition-colors"
                >
                  <Copy size={18} />
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </button>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setIsTrialModalOpen(false); setGeneratedUrl(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-rs-salmon/20  rounded hover:bg-rs-salmon/30 transition-colors"
              >
                <X size={18} />
                Close
              </button>
              <button
                onClick={handleGenerate}
                className="flex items-center gap-2 px-4 py-2 bg-rs-dark-green !text-rs-black rounded hover:bg-rs-green transition-colors"
              >
                <LinkIcon size={18} />
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopNavbar;

